import * as React from 'react'
import * as TabsPrimitive from '@radix-ui/react-tabs'

import { cn } from '../../lib/utils'
import { useIsScrollable } from '../../hooks/useIsScrollable'

function Tabs({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      className={cn('flex flex-col gap-2', className)}
      {...props}
    />
  )
}

function TabsList({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.List>) {
  const ref = React.useRef<HTMLDivElement>(null)
  const isScrollable = useIsScrollable(ref)

  return (
    <TabsPrimitive.List
      ref={ref}
      data-slot="tabs-list"
      className={cn(
        'overflow-x-auto w-full md:bg-transparent md:w-fit md:gap-0 text-muted-foreground inline-flex h-auto items-center justify-start md:justify-center p-[8px] mb-4 md:mb-0',
        // Add scrollable-specific styling here
        isScrollable && 'bg-main/20 md:bg-transparent rounded-l-md',
        className,
      )}
      {...props}
    />
  )
}

function TabsTrigger({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        'data-[state=active]:bg-main data-[state=active]:border-none data-[state=active]:text-main-foreground dark:data-[state=active]:text-main-foreground dark:data-[state=active]:border-input dark:data-[state=active]:bg-input/30 data-[state=active]:shadow-sm',
        'cursor-pointer text-card-text bg-background dark:text-muted-foreground inline-flex h-[calc(100%-1px)] md:flex-1 items-center justify-center gap-1.5 rounded-md border border-card-border px-2 py-1 text-md md:text-sm font-medium whitespace-nowrap transition-[color,box-shadow] mr-2 md:mb-0',
        'disabled:pointer-events-none disabled:opacity-50',
        'hover:border-card-text hover:bg-card-border/10 hover:text-card-text  transition-all duration-100 ease-in-out',
        'px-2.5 py-2 md:px-3 md:py-2.5',
        className,
      )}
      {...props}
    />
  )
}

function TabsContent({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn('flex-1 outline-none', className)}
      {...props}
    />
  )
}

function TabsContainer({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'flex flex-col md:flex-row flex-wrap md:items-center md:justify-between',
        className,
      )}
      {...props}
    />
  )
}

function TabsHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-0',
        className,
      )}
      {...props}
    />
  )
}

function TabsHeaderTitle({ className, ...props }: React.ComponentProps<'h2'>) {
  return <h2 className={cn('text-lg md:text-2xl font-bold', className)} {...props} />
}

function TabsHeaderActions({ className, ...props }: React.ComponentProps<'div'>) {
  return <div className={cn('flex gap-4 flex-wrap md:flex-nowrap', className)} {...props} />
}

export {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  TabsContainer,
  TabsHeader,
  TabsHeaderTitle,
  TabsHeaderActions,
}
